import { format } from 'date-fns'
import { Download } from 'lucide-react'
import { useState } from 'react'
import type { SlotMetrics, TopLink } from '@/api/hooks/use-campaign-analytics'
import { useAnalyticsExport, useCampaignSlots } from '@/api/hooks/use-campaign-analytics'
import { useCampaigns } from '@/api/hooks/use-campaigns'
import { DataTable } from '@/components/ui/data-table'
import { MetricCard } from '@/components/ui/metric-card'
import { StatusBadge } from '@/components/ui/status-badge'

function pct(n: number, d: number): string {
  if (!d) return '0.00%'
  return `${((n / d) * 100).toFixed(2)}%`
}

export function AnalyticsByCampaign(): React.JSX.Element {
  const [selectedId, setSelectedId] = useState('')
  const { data: campaignList, isLoading: loadingCampaigns } = useCampaigns({ per_page: 100 })
  const { data: slotData, isLoading: loadingSlots } = useCampaignSlots(selectedId)
  const exportMutation = useAnalyticsExport()

  const campaigns = campaignList?.data ?? []
  const selected = campaigns.find((c) => c.id === selectedId)
  const totalSent = slotData?.total_sent ?? 0
  const slots = slotData?.slots ?? []
  const topLinks = slotData?.top_links ?? []
  const totalClicks = slots.reduce((s, r) => s + (r.clicks ?? 0), 0)
  const totalUnique = slots.reduce((s, r) => s + (r.unique_clicks ?? 0), 0)

  function handleCsvDownload(): void {
    if (!selected) return
    exportMutation.mutate({
      type: 'campaign',
      campaign_id: selected.id,
      from: format(new Date(selected.created_at), 'yyyy-MM-dd'),
      to: format(new Date(), 'yyyy-MM-dd'),
      format: 'csv',
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          disabled={loadingCampaigns}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
        >
          <option value="">Select a campaign...</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        {selectedId && slotData && (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={handleCsvDownload}
              disabled={exportMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              {exportMutation.isPending ? 'Exporting...' : 'CSV'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!selected) return
                exportMutation.mutate({
                  type: 'campaign',
                  campaign_id: selected.id,
                  from: format(new Date(selected.created_at), 'yyyy-MM-dd'),
                  to: format(new Date(), 'yyyy-MM-dd'),
                  format: 'pdf',
                })
              }}
              disabled={exportMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              PDF
            </button>
          </div>
        )}
      </div>

      {!selectedId && (
        <p className="py-12 text-center text-sm text-text-tertiary">
          Select a campaign to view its analytics.
        </p>
      )}

      {selectedId && loadingSlots && (
        <p className="py-8 text-center text-sm text-text-tertiary">Loading analytics...</p>
      )}

      {selectedId && slotData && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <MetricCard label="Sent" value={totalSent.toLocaleString()} />
            <MetricCard label="Total Clicks" value={totalClicks.toLocaleString()} />
            <MetricCard label="Unique Clicks" value={totalUnique.toLocaleString()} />
            <MetricCard label="CTR" value={pct(totalUnique, totalSent)} />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-medium text-text-secondary">Slot Performance</h3>
            {slots.length === 0 ? (
              <p className="py-6 text-center text-sm text-text-tertiary">
                No click data yet for this campaign.
              </p>
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'slot',
                    label: 'Slot',
                    render: (r: SlotMetrics) => <span className="font-medium">{r.slot}</span>,
                  },
                  {
                    key: 'type',
                    label: 'Type',
                    render: (r: SlotMetrics) => (
                      <StatusBadge status={r.type === 'ad' ? 'pending' : 'active'}>
                        {r.type}
                      </StatusBadge>
                    ),
                  },
                  {
                    key: 'clicks',
                    label: 'Clicks',
                    render: (r: SlotMetrics) => (r.clicks ?? 0).toLocaleString(),
                  },
                  {
                    key: 'unique_clicks',
                    label: 'Unique',
                    render: (r: SlotMetrics) => (r.unique_clicks ?? 0).toLocaleString(),
                  },
                  {
                    key: 'ctr',
                    label: 'CTR',
                    render: (r: SlotMetrics) => `${(r.ctr ?? 0).toFixed(2)}%`,
                  },
                ]}
                data={slots}
              />
            )}
          </div>

          <div>
            <h3 className="mb-3 text-sm font-medium text-text-secondary">Top Clicked Links</h3>
            {topLinks.length === 0 ? (
              <p className="py-6 text-center text-sm text-text-tertiary">
                No link clicks recorded yet.
              </p>
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'url',
                    label: 'URL',
                    render: (r: TopLink) => (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="max-w-xs truncate text-brand-500 hover:underline"
                      >
                        {r.url}
                      </a>
                    ),
                  },
                  { key: 'slot', label: 'Slot', render: (r: TopLink) => r.slot },
                  {
                    key: 'clicks',
                    label: 'Clicks',
                    render: (r: TopLink) => (r.clicks ?? 0).toLocaleString(),
                  },
                  {
                    key: 'unique_clicks',
                    label: 'Unique',
                    render: (r: TopLink) => (r.unique_clicks ?? 0).toLocaleString(),
                  },
                ]}
                data={topLinks}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
